import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { CreateMissionRequest, UpdateMissionRequest, InsertMissionSegment } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

export function useMissions() {
  return useQuery({
    queryKey: [api.missions.list.path],
    queryFn: async () => {
      const res = await authFetch(api.missions.list.path);
      if (!res.ok) throw new Error("Failed to fetch missions");
      return res.json();
    },
  });
}

export function useMission(id: string) {
  return useQuery({
    queryKey: [api.missions.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.missions.get.path, { id });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch mission");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateMissionRequest) => {
      const res = await authFetch(api.missions.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        let body: any = {};
        try { body = await res.json(); } catch { /* pas de JSON */ }
        throw new Error(`${res.status}: ${JSON.stringify(body)}`);
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.missions.list.path] }),
  });
}

export function useUpdateMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & UpdateMissionRequest) => {
      const url = buildUrl(api.missions.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        let body: any = {};
        try { body = await res.json(); } catch { /* pas de JSON */ }
        throw new Error(`${res.status}: ${JSON.stringify(body)}`);
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.missions.list.path] }),
  });
}

export function useMissionSegments(missionId: string) {
  return useQuery({
    queryKey: ['/api/missions', missionId, 'segments'],
    queryFn: async () => {
      const res = await authFetch(`/api/missions/${missionId}/segments`);
      if (!res.ok) throw new Error("Failed to fetch segments");
      return res.json();
    },
    enabled: !!missionId,
  });
}

export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ missionId, ...data }: { missionId: string } & Omit<InsertMissionSegment, 'missionId'>) => {
      const res = await authFetch(`/api/missions/${missionId}/segments`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create segment");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/missions', variables.missionId, 'segments'] });
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, missionId, ...updates }: { id: string; missionId: string } & Partial<InsertMissionSegment>) => {
      const res = await authFetch(`/api/mission-segments/${id}`, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update segment");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/missions', variables.missionId, 'segments'] });
    },
  });
}

export function useDeleteMission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/missions/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete mission");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.missions.list.path] }),
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, missionId }: { id: string; missionId: string }) => {
      const res = await authFetch(`/api/mission-segments/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete segment");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/missions', variables.missionId, 'segments'] });
    },
  });
}
